import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Loader2,
  LogOut,
  Mail,
  RefreshCw,
  Send,
  Sparkles,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import StarField from "@/components/StarField";
import { apiJson, clearWebId, logout, readWebId, saveWebId, shortEmail } from "@/lib/auth";
import { useSeo } from "@/lib/seo";
import { API_BASE } from "@/config/links";
import BOT_AVATAR_URL from "@/assets/hero-avatar.webp";

const POLL_MS = 2000;

// Бот отвечает markdown-разметкой (той же, что уходит в MAX) — нужен только минимум:
// жирный, курсив, зачёркнутый. HTML из ответа не рендерим.
function renderMarkdown(text) {
  const escaped = String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/~~([^~]+)~~/g, "<del>$1</del>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>")
    .replace(/https?:\/\/[^\s<&]+/g, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
}

function formatCreated(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });
}

const MyStories = ({ state, onOpen, onRefresh }) => {
  if (state.status === "anon") {
    return (
      <div className="rounded-3xl border border-line bg-white/[0.04] p-5 text-center">
        <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
          Оставьте почту, чтобы видеть сохранённые сказки.
        </p>
        <Link
          to="/login?next=/chat"
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-amber px-5 text-sm font-semibold text-night-900 shadow-glow transition-colors hover:bg-amber-hover"
        >
          Оставить почту
        </Link>
      </div>
    );
  }

  if (state.status === "loading") {
    return (
      <div className="flex items-center gap-2 px-1 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загружаю сказки
      </div>
    );
  }

  if (!state.stories.length) {
    return (
      <div className="rounded-3xl border border-dashed border-line-strong p-5 text-center text-sm leading-relaxed text-muted-foreground">
        Здесь появятся ваши сказки
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onRefresh}
        className="inline-flex items-center gap-1.5 self-end text-xs text-dim transition-colors hover:text-amber-soft"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Обновить
      </button>
      {state.stories.map((story) => (
        <button
          key={story.id}
          type="button"
          onClick={() => onOpen(story)}
          className="group flex gap-3 rounded-2xl border border-line bg-white/[0.04] p-3 text-left transition-colors hover:border-amber/40"
        >
          {story.imageUrl ? (
            <img
              src={story.imageUrl}
              alt=""
              loading="lazy"
              className="h-14 w-14 shrink-0 rounded-xl object-cover"
            />
          ) : (
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-amber/10 text-amber">
              <BookOpen className="h-5 w-5" />
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate font-serif text-base text-cream">
              {story.title || "Сказка"}
            </span>
            <span className="mt-0.5 block text-xs text-dim">{formatCreated(story.createdAt)}</span>
          </span>
        </button>
      ))}
    </div>
  );
};

const ChatPage = () => {
  useSeo({
    title: "Сделать сказку | Сказки детям на ночь",
    description: "Расскажите про ребёнка и тему — и получите персональную сказку с озвучкой за пару минут.",
    path: "/chat",
    noindex: true,
  });

  const [params] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [waiting, setWaiting] = useState(true);
  const [error, setError] = useState("");
  const [auth, setAuth] = useState({ loading: true, email: null });
  const [storiesState, setStoriesState] = useState({ status: "loading", stories: [] });
  const [openStory, setOpenStory] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const email = auth.email;

  const webIdRef = useRef(null);
  const cursorRef = useRef(0);
  const pollingRef = useRef(false);
  const bottomRef = useRef(null);

  const loadStories = useCallback(async (silent = false) => {
    if (!silent) setStoriesState((prev) => ({ ...prev, status: "loading" }));
    try {
      const data = await apiJson("/api/web/my-stories");
      setStoriesState({ status: "ready", stories: data.stories || [] });
    } catch (e) {
      setStoriesState({ status: e.status === 401 ? "anon" : "ready", stories: [] });
    }
  }, []);

  // Одновременно идут два опроса — по таймеру и сразу после отправки. Без флага
  // оба уходят с одним курсором и вставляют историю дважды.
  const poll = useCallback(async () => {
    const webId = webIdRef.current;
    if (!webId || pollingRef.current) return;
    pollingRef.current = true;
    try {
      const { messages: incoming } = await apiJson("/api/web/poll", {
        method: "POST",
        body: JSON.stringify({ webId, after: cursorRef.current }),
      });
      if (!incoming?.length) return;
      cursorRef.current = Math.max(cursorRef.current, incoming[incoming.length - 1].seq);
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.seq));
        const fresh = incoming.filter((m) => !seen.has(m.seq)).map((m) => ({ ...m, from: "bot" }));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      setWaiting(false);
      // Готовая сказка приходит сообщением — панель «Мои сказки» подтягиваем следом.
      void loadStories(true);
    } catch {
      setError("Связь прервалась. Обновите страницу.");
    } finally {
      pollingRef.current = false;
    }
  }, [loadStories]);

  useEffect(() => {
    let cancelled = false;
    apiJson("/api/web/auth/me")
      .then((me) => {
        if (!cancelled) setAuth({ loading: false, email: me.email || null });
      })
      .catch(() => {
        if (!cancelled) setAuth({ loading: false, email: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Чат — только после входа: без почты сказку всё равно некуда сохранить,
  // поэтому сессию не открываем вовсе.
  useEffect(() => {
    if (!email) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const existing = readWebId();
        const session = await apiJson("/api/web/session", {
          method: "POST",
          body: JSON.stringify({
            webId: existing || undefined,
            startPayload: existing ? undefined : params.get("start") || undefined,
          }),
        });
        if (cancelled) return;
        webIdRef.current = session.webId;
        saveWebId(session.webId);
        if (session.email) setAuth({ loading: false, email: session.email });
        await poll();
        // После входа последнее сообщение бота — просьба войти; просим свежее меню,
        // чтобы человек сразу видел, что делать дальше.
        if (params.get("menu")) {
          window.history.replaceState(null, "", "/chat");
          await say({ type: "callback", action: "menu" });
        }
      } catch {
        if (!cancelled) {
          setWaiting(false);
          setError("Не удалось открыть чат. Попробуйте обновить страницу.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll, email]);

  useEffect(() => {
    if (!email) return undefined;
    const id = window.setInterval(poll, POLL_MS);
    return () => window.clearInterval(id);
  }, [poll, email]);

  useEffect(() => {
    if (!email) return;
    void loadStories();
  }, [loadStories, email]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, waiting]);

  const say = useCallback(
    async (payload, echo) => {
      const webId = webIdRef.current;
      if (!webId) return;
      if (echo) {
        setMessages((prev) => [...prev, { seq: `local_${Date.now()}`, from: "user", text: echo }]);
      }
      setWaiting(true);
      setError("");
      try {
        await apiJson("/api/web/say", {
          method: "POST",
          body: JSON.stringify({ webId, ...payload }),
        });
        await poll();
      } catch {
        setWaiting(false);
        setError("Сообщение не отправилось. Попробуйте ещё раз.");
      }
    },
    [poll],
  );

  const sendText = () => {
    const value = draft.trim();
    if (!value) return;
    setDraft("");
    void say({ type: "text", text: value }, value);
  };

  // webId — это и есть доступ к аккаунту, после выхода его нельзя оставлять в браузере.
  const onLogout = async () => {
    await logout().catch(() => {});
    clearWebId();
    window.location.replace("/chat");
  };

  if (auth.loading || !email) {
    return (
      <div className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-night-900">
        <div className="absolute inset-0 bg-gradient-to-b from-night-950 via-night-900 to-night-800" />
        <StarField />
        <div className="glow-warm pointer-events-none absolute inset-0" />

        <div className="relative flex flex-1 items-center justify-center px-4 py-12 sm:px-6">
          {auth.loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-amber" />
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-md rounded-3xl border border-line bg-white/[0.05] p-7 text-center shadow-lift backdrop-blur-2xl sm:p-9"
            >
              <span className="relative mb-5 inline-flex">
                <span className="absolute -inset-2 rounded-full bg-amber/25 blur-lg" />
                <span className="relative flex h-14 w-14 items-center justify-center rounded-full border border-amber/30 bg-night-900 text-amber">
                  <Mail className="h-6 w-6" />
                </span>
              </span>
              <h1 className="font-serif text-3xl text-cream sm:text-4xl">Сначала оставьте почту</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Пришлём код из шести цифр, и сказки сохранятся на вашей полке
              </p>
              <Link
                to="/login?next=/chat"
                className="mt-7 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-amber px-6 font-semibold text-night-900 shadow-glow transition-all duration-300 hover:bg-amber-hover hover:shadow-glow-lg"
              >
                Оставить почту
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-amber-soft"
              >
                <ArrowLeft className="h-4 w-4" />
                На главную
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-[100dvh] flex-col overflow-hidden bg-night-900">
      <div className="absolute inset-0 bg-gradient-to-b from-night-950 via-night-900 to-night-800" />
      <StarField density={0.00009} />

      <header className="relative z-20 flex h-16 shrink-0 items-center gap-3 border-b border-line bg-night-900/80 px-3 backdrop-blur-xl sm:px-5">
        <Link
          to="/"
          aria-label="На главную"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line text-muted-foreground transition-colors hover:border-amber/50 hover:text-amber-soft"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <img
          src={BOT_AVATAR_URL}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full border border-line object-cover"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-serif text-lg leading-tight text-cream">Сказка для ребёнка</p>
          <p className="truncate text-xs text-dim">{shortEmail(email)}</p>
        </div>

        <Sheet open={panelOpen} onOpenChange={(v) => { setPanelOpen(v); if (v) void loadStories(); }}>
          <SheetTrigger
            aria-label="Мои сказки"
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-line px-3 text-xs font-medium text-muted-foreground transition-colors hover:border-amber/50 hover:text-amber-soft lg:hidden"
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Мои сказки</span>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-[86vw] overflow-y-auto border-l border-line bg-night-900/95 backdrop-blur-xl sm:max-w-sm"
          >
            <SheetTitle className="mb-5 mt-2 font-serif text-2xl font-normal text-cream">Мои сказки</SheetTitle>
            <MyStories
              state={storiesState}
              onOpen={(s) => {
                setPanelOpen(false);
                setOpenStory(s);
              }}
              onRefresh={loadStories}
            />
          </SheetContent>
        </Sheet>

        <button
          type="button"
          onClick={onLogout}
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-line px-3 text-xs font-medium text-muted-foreground transition-colors hover:border-amber/50 hover:text-amber-soft"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Выйти</span>
        </button>
      </header>

      <div className="relative z-10 flex min-h-0 flex-1">
        <main className="flex min-w-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-3 py-6 sm:px-5">
            <div className="mx-auto flex max-w-2xl flex-col gap-4">
              {messages.map((m, i) => (
                <div key={m.seq} className={m.from === "user" ? "flex justify-end" : "flex gap-2.5"}>
                  {m.from === "bot" ? (
                    <img
                      src={BOT_AVATAR_URL}
                      alt=""
                      className="mt-1 h-8 w-8 shrink-0 self-end rounded-full border border-line object-cover"
                    />
                  ) : null}

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className={`max-w-[85%] rounded-3xl px-4 py-3 text-[15px] leading-relaxed sm:max-w-[78%] ${
                      m.from === "user"
                        ? "rounded-br-lg bg-amber text-night-900"
                        : "rounded-bl-lg border border-line bg-white/[0.06] text-cream backdrop-blur-xl"
                    }`}
                  >
                    {m.text ? (
                      <span
                        className="whitespace-pre-wrap break-words [&_a]:text-amber-soft [&_a]:underline [&_a]:underline-offset-2"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }}
                      />
                    ) : null}

                    {m.imageUrl ? (
                      <img
                        src={m.imageUrl}
                        alt="Иллюстрация к сказке"
                        loading="lazy"
                        className="mt-3 w-full rounded-2xl"
                      />
                    ) : null}
                    {m.audioUrl ? <audio controls src={m.audioUrl} className="mt-3 w-full" /> : null}
                    {m.videoUrl ? (
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        src={m.videoUrl}
                        className="mt-3 w-full rounded-2xl bg-black"
                      />
                    ) : null}

                    {m.from === "bot" && m.buttons?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {m.buttons.flat().map((b, bi) => {
                          const key = `${m.seq}_${b.text}_${bi}`;
                          const chip =
                            "inline-flex items-center rounded-full border border-amber/35 bg-amber/10 px-3.5 py-2 text-sm font-medium text-amber-soft transition-colors hover:bg-amber/20 disabled:opacity-50";
                          if (b.url) {
                            if (!/^(https?:\/\/|\/(?!\/))/.test(b.url)) return null;
                            return b.url.startsWith("/") ? (
                              <Link key={key} to={b.url} className={chip}>
                                {b.text}
                              </Link>
                            ) : (
                              <a
                                key={key}
                                href={b.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={chip}
                              >
                                {b.text}
                              </a>
                            );
                          }
                          return (
                            <button
                              key={key}
                              type="button"
                              disabled={waiting}
                              onClick={() => say({ type: "callback", action: b.action }, b.text)}
                              className={chip}
                            >
                              {b.text}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </motion.div>
                </div>
              ))}

              {waiting ? (
                <div className="flex items-end gap-2.5" data-testid="chat-typing">
                  <img
                    src={BOT_AVATAR_URL}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-full border border-line object-cover"
                  />
                  <div className="flex items-center gap-1.5 rounded-3xl rounded-bl-lg border border-line bg-white/[0.06] px-4 py-4 backdrop-blur-xl">
                    {[0, 1, 2].map((d) => (
                      <span
                        key={d}
                        className="h-2 w-2 animate-typing rounded-full bg-amber-soft"
                        style={{ animationDelay: `${d * 0.16}s` }}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {error ? (
                <p className="rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {error}
                </p>
              ) : null}

              <div ref={bottomRef} />
            </div>
          </div>

          <div className="shrink-0 border-t border-line bg-night-900/85 px-3 py-3 backdrop-blur-xl sm:px-5">
            <div className="mx-auto flex max-w-2xl items-end gap-2 rounded-3xl border border-line bg-white/[0.05] p-2">
              <textarea
                rows={1}
                value={draft}
                placeholder="Напишите сообщение…"
                aria-label="Сообщение"
                data-ym-disable-keys="true"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendText();
                  }
                }}
                className="max-h-36 flex-1 resize-none bg-transparent px-3 py-2.5 text-[15px] text-cream placeholder:text-dim focus:outline-none"
              />
              <button
                type="button"
                onClick={sendText}
                disabled={!draft.trim()}
                aria-label="Отправить"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber text-night-900 shadow-glow transition-all hover:bg-amber-hover disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="mx-auto mt-2 max-w-2xl text-center text-[11px] text-dim">
              Сказки пишет искусственный интеллект. Оплата через ЮKassa.{" "}
              <a href="/oferta" className="underline underline-offset-2 hover:text-amber-soft">Оферта</a>
              {" · "}
              <a href="/privacy" className="underline underline-offset-2 hover:text-amber-soft">Конфиденциальность</a>
              {" · удалить данные — команда /delete_me"}
            </p>
          </div>
        </main>

        <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-line bg-night-950/40 px-5 py-6 backdrop-blur-xl lg:block">
          <div className="mb-5 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber" />
            <h2 className="font-serif text-2xl text-cream">Мои сказки</h2>
          </div>
          <MyStories state={storiesState} onOpen={setOpenStory} onRefresh={loadStories} />
        </aside>
      </div>

      <Dialog open={Boolean(openStory)} onOpenChange={(v) => !v && setOpenStory(null)}>
        <DialogContent aria-describedby={undefined} className="max-h-[85vh] max-w-2xl overflow-y-auto border-line bg-night-card sm:rounded-3xl">
          <DialogTitle className="font-serif text-2xl leading-snug text-cream">
            {openStory?.title || "Сказка"}
          </DialogTitle>
          {openStory?.imageUrl ? (
            <img
              src={openStory.imageUrl}
              alt=""
              className="w-full rounded-2xl border border-line object-cover"
            />
          ) : null}
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-muted-foreground">
            {openStory?.text}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatPage;
